import { ChevronDown, ChevronUp, Edit2, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { CapacityBracket } from '../../../types/capacity-brackets.types';
import { formatPrice, getDurationLabel } from '../../../utils/capacity-brackets.utils';
import { BracketForm } from './BracketForm';

const iconBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px',
  background: 'transparent',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  cursor: 'pointer',
  color: '#6b7280',
  transition: 'all 0.15s ease',
};

export function BracketRow({
  bracket,
  onUpdate,
  onDelete,
  data,
}: {
  bracket: CapacityBracket;
  onUpdate: (updated: CapacityBracket) => void;
  onDelete: () => void;
  data: any;
}) {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Filtrer les prix basés sur les réglages de l'étape 4
  const visiblePrices = bracket.prices.filter((p) => {
    if (bracket.bracketType === 'forfait') return p.durationType === 'FORFAIT';
    if (p.durationType === 'HALF_DAY') return data.allowHalfDay !== false;
    if (p.durationType === 'FULL_DAY') return data.allowFullDay !== false;
    if (p.durationType === 'MONTH') return !!data.allowMonth;
    return true;
  });

  if (editing) {
    return (
      <BracketForm
        initial={bracket}
        submitLabel="Enregistrer"
        onSubmit={(updated) => {
          onUpdate(updated);
          setEditing(false);
        }}
        onCancel={() => setEditing(false)}
        data={data}
      />
    );
  }

  return (
    <div
      style={{
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', gap: '12px' }}>
        <Users size={18} style={{ color: '#00758d', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>{bracket.label}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
            {bracket.minCapacity}–{bracket.maxCapacity} pers. {bracket.roomName && ` · ${bracket.roomName}`} ·{' '}
            {bracket.pricingUnit === 'per_person' ? 'par personne' : 'par groupe'}
          </div>
        </div>
        <div style={{ textAlign: 'right', marginRight: '8px' }}>
          {visiblePrices.map((p, i) => (
            <div key={i} style={{ fontSize: '13px', color: '#00758d', fontWeight: 600 }}>
              {getDurationLabel(p.durationType)} : {formatPrice(p.price, bracket.pricingUnit)}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
          <button type="button" title="Développer" style={iconBtnStyle} onClick={() => setExpanded((v) => !v)}>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <button type="button" title="Modifier" style={iconBtnStyle} onClick={() => setEditing(true)}>
            <Edit2 size={15} />
          </button>
          <button type="button" title="Supprimer" style={{ ...iconBtnStyle, color: '#ef4444' }} onClick={onDelete}>
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      {expanded && (
        <div
          style={{
            padding: '12px 16px 16px',
            borderTop: '1px solid #f3f4f6',
            background: '#f9fafb',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '8px',
            fontSize: '13px',
          }}
        >
          <div>
            <span style={{ color: '#6b7280' }}>Type :</span>{' '}
            {bracket.bracketType === 'forfait' ? 'Forfait' : 'Par durée'}
          </div>
          <div>
            <span style={{ color: '#6b7280' }}>Unité :</span>{' '}
            {bracket.pricingUnit === 'per_person' ? 'Par personne' : 'Par groupe'}
          </div>
          <div>
            <span style={{ color: '#6b7280' }}>Capacité :</span> {bracket.minCapacity} – {bracket.maxCapacity} pers.
          </div>
          {bracket.roomName && (
            <div>
              <span style={{ color: '#6b7280' }}>Salle :</span> {bracket.roomName}
            </div>
          )}
          {visiblePrices.map((p, i) => (
            <div key={i}>
              <span style={{ color: '#6b7280' }}>{getDurationLabel(p.durationType)} :</span>{' '}
              <strong style={{ color: '#00758d' }}>{formatPrice(p.price, bracket.pricingUnit)}</strong>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
